import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { StateEntityGroupsService } from '../../state-entity-groups.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-external-state-entity-edit',
    templateUrl: './external-state-entity-edit.component.html',
    styleUrls: ['./external-state-entity-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ExternalStateEntityEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  modalTitle = 'Edit State Entity';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showEntityLoader = true;
  isCreateMode = false;

  rowData: any;
  gridData: any[] = [];
  filterParams: any = {};
  baseUrl: string = '';

  external_entity_list: any[] = [];
  loaded_entity_list: any[] = [];
  selectedRowIndex: number = 0;

  meList: any[] = [];
  leidList: any[] = [];
  hoList: any[] = [];
  allUSStates: any[] = [];
  leType: any[] = [
    { id: 'M', name: 'Modification' },
    { id: 'E', name: 'Elimination' }
  ];

  USER_SETTINGS: any;
  GENERAL_CONFIG: any;
  globalService: any;

  private leid_cdr_available = false;
  private rptr_available = false;
  private initCalled = false;

  constructor(
    private stateEntityService: StateEntityGroupsService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  private tryInit(): void {
    if (this.initCalled || !this.baseUrl) {
      return;
    }

    const hasFilterContext =
      (this.filterParams && Object.keys(this.filterParams).length > 0) ||
      (this.globalService?.globalParams && Object.keys(this.globalService.globalParams).length > 0);
    const hasHeaderData = !!(this.globalService && this.globalService.workspaceFactory);

    if (this.isCreateMode) {
      if (!hasFilterContext || !hasHeaderData) {
        return;
      }
    } else {
      if (!this.gridData?.length || !hasFilterContext || !hasHeaderData) {
        return;
      }
    }

    this.initCalled = true;
    this.init();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetInitState();
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetInitState();
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private resetInitState(): void {
    this.initCalled = false;
    this.leid_cdr_available = false;
    this.rptr_available = false;
    this.showEntityLoader = true;
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseUrl = this.GENERAL_CONFIG.base_url || '';
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    const callbackName =
      activeScreen?.data?.modalName
      || activeScreen?.data?.modal_name
      || activeScreen?.data?.colData?.call_back
      || activeScreen?.data?.mode
      || '';
    this.isCreateMode = callbackName.toLowerCase().includes('create');
    this.modalTitle = this.isCreateMode ? 'Create State Entity' : 'Edit State Entity';

    this.applyGridPayload(gridPayloadFromActiveScreen(activeScreen));
    this.globalService = {
      ...header,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      filterParams: this.filterParams,
    };
    this.cdr.markForCheck();
  }

  private applyGridPayload(grid: any): void {
    if (!grid) {
      this.gridData = [];
      return;
    }
    if (Array.isArray(grid) && grid.length > 0) {
      this.gridData = _.cloneDeep(grid);
    } else if (typeof grid === 'object' && grid.rowData && Object.keys(grid.rowData).length > 0) {
      this.rowData = grid.rowData;
      this.gridData = [{ data: grid.rowData }];
    } else {
      this.gridData = [];
    }
  }

  init(): void {
    try {
      this.leid_cdr_available = false;
      this.rptr_available = false;
      this.showEntityLoader = true;
      if (!this.baseUrl) {
        return;
      }
      if (this.isCreateMode || (this.gridData && this.gridData.length > 0)) {
        this.fetchMe();
        this.fetchLeidCdr();
        this.fetchUSStateList();
        this.fetchReportingPeriod();
      }
    } catch (e) {
      console.error('Error in external state entity edit init:', e);
      this.showEntityLoader = false;
      this.cdr.detectChanges();
    }
  }

  private onReferenceDataReady(): void {
    if (!this.leid_cdr_available || !this.rptr_available) {
      return;
    }
    if (this.isCreateMode) {
      this.showEntityLoader = false;
    } else {
      this.prepareModalData();
    }
    this.cdr.detectChanges();
  }

  private showFetchError(message: string): void {
    this.gtwSnackbar.openSnackBar(message, 'gtw-snackbar--red', true, 4);
    this.showEntityLoader = false;
    this.cdr.detectChanges();
  }

  fetchMe(): void {
    this.stateEntityService.fetchMeList(this.baseUrl, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.meList = [];
        if (data.jsonObject) {
          data.jsonObject.forEach((item: any) => {
            this.meList.push({
              id: item.ME_KEY,
              label: item.ME_NAME + ' - ' + item.ME_CODE,
              me_code: item.ME_CODE,
              me_key: item.ME_KEY
            });
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error fetching ME list:', error);
      }
    );
  }

  fetchLeidCdr(): void {
    this.stateEntityService.fetchLeidCdrList(this.baseUrl, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.leid_cdr_available = true;
        this.leidList = [];
        if (data.jsonObject) {
          data.jsonObject.forEach((item: any) => {
            this.leidList.push({
              id: item.HO_LEID,
              label: item.HO_LEID,
              cdr: item.HO_CDR.split(','),
              internal_flag: item.HO_INTERNAL_YN,
              me_code: item.ME_CODE,
              me_key: item.ME_KEY
            });
          });
        }
        this.onReferenceDataReady();
        this.cdr.detectChanges();
      },
      () => {
        this.leid_cdr_available = true;
        this.showFetchError('Failed to load HO LEID list. Please try again.');
      }
    );
  }

  fetchReportingPeriod(): void {
    this.stateEntityService.fetchReportingPeriod(this.baseUrl, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.rptr_available = true;
        this.hoList = data.jsonObject || [];
        this.onReferenceDataReady();
        this.cdr.detectChanges();
      },
      () => {
        this.rptr_available = true;
        this.showFetchError('Failed to load reporting period list. Please try again.');
      }
    );
  }

  fetchUSStateList(): void {
    this.stateEntityService.fetchUSStateList(this.baseUrl, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.allUSStates = data.jsonObject || [];
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error fetching US state list:', error);
      }
    );
  }

  prepareModalData(): void {
    this.external_entity_list = [];

    this.gridData.forEach((row: any, key: number) => {
      const rowData = row?.data ?? row;
      const temp_cdr: any[] = [];
      const LeidIndex = _.findIndex(this.leidList, { id: rowData.HO_LEID });

      if (LeidIndex !== -1) {
        this.leidList[LeidIndex].cdr.forEach((data: string) => {
          temp_cdr.push({ id: data, label: data });
        });
      }

      const reportingPeriodList: any[] = [];
      this.hoList.forEach((data: any) => {
        if (data.HO_LEID == rowData.HO_LEID && data.HO_CDR_NO == rowData.HO_CDR_NO) {
          const temp_reporting_period = data.REPORTING_PERIOD.split(',');
          temp_reporting_period.forEach((period: string) => {
            reportingPeriodList.push({ id: period, label: period });
          });
        }
      });

      const meIndex = _.findIndex(this.meList, { id: rowData.ME_KEY });
      const cdrIndex = _.findIndex(temp_cdr, { id: rowData.HO_CDR_NO });
      const reportingPeriodIndex = _.findIndex(reportingPeriodList, {
        id: rowData.HO_REPORTING_PERIOD ? rowData.HO_REPORTING_PERIOD.toString() : ''
      });

      const temp_data = {
        occurance: key,
        le_key: rowData.LE_KEY,
        leid: rowData.LEID,
        le_name: rowData.LE_NAME,
        ein: rowData.EIN,
        address_line1: rowData.ADDRESS_LINE1,
        address_line2: rowData.ADDRESS_LINE2,
        address_city: rowData.ADDRESS_CITY,
        address_state: rowData.ADDRESS_STATE,
        address_zip: rowData.ADDRESS_ZIP,
        le_type: rowData.LE_TYPE,
        me_key: meIndex !== -1 ? this.meList[meIndex] : null,
        ho_leid: LeidIndex !== -1 ? this.leidList[LeidIndex] : null,
        ho_cdr_no: cdrIndex !== -1 ? temp_cdr[cdrIndex] : null,
        ho_reporting_period: reportingPeriodIndex !== -1 ? reportingPeriodList[reportingPeriodIndex] : null,
        state_domicile: rowData.STATE_DOMICILE,
        cdr_list: temp_cdr,
        reporting_period_list: reportingPeriodList,
        is_me_visible: LeidIndex !== -1 ? false : true
      };

      this.external_entity_list.push(temp_data);
    });

    this.loaded_entity_list = _.cloneDeep(this.external_entity_list);
    this.showEntityLoader = false;
    this.cdr.detectChanges();
  }

  entitySelection(k: number): void {
    const index = _.findIndex(this.external_entity_list, { occurance: k });
    this.selectedRowIndex = index;
  }

  onLeidChange(item: any, entity: any): void {
    if (item) {
      const cdrList: any[] = [];
      item.cdr.forEach((data: string) => {
        cdrList.push({ id: data, label: data });
      });
      entity.ho_cdr_no = null;
      entity.cdr_list = cdrList;
      entity.ho_reporting_period = null;
      entity.reporting_period_list = [];
      entity.me_key = null;
      entity.is_me_visible = false;
    } else {
      entity.ho_cdr_no = null;
      entity.cdr_list = [];
      entity.ho_reporting_period = null;
      entity.reporting_period_list = [];
      entity.is_me_visible = true;
    }
    this.cdr.detectChanges();
  }

  onCdrChange(item: any, entity: any): void {
    if (item && entity.ho_leid) {
      const reportingPeriodList: any[] = [];
      this.hoList.forEach((data: any) => {
        if (data.HO_LEID == entity.ho_leid.id && data.HO_CDR_NO == item.id) {
          const temp_reporting_period = data.REPORTING_PERIOD.split(',');
          temp_reporting_period.forEach((period: string) => {
            reportingPeriodList.push({ id: period, label: period });
          });
        }
      });
      entity.reporting_period_list = reportingPeriodList;
      entity.ho_reporting_period = null;
    } else {
      entity.ho_reporting_period = null;
      entity.reporting_period_list = [];
    }
    this.cdr.detectChanges();
  }

  addRows(): void {
    const temp_entity = {
      occurance: this.external_entity_list.length,
      le_key: 'NEW',
      leid: '',
      le_name: '',
      ein: '',
      address_line1: '',
      address_line2: '',
      address_city: '',
      address_state: '',
      address_zip: '',
      le_type: '',
      me_key: null,
      ho_leid: null,
      ho_cdr_no: null,
      ho_reporting_period: null,
      state_domicile: '',
      cdr_list: [],
      reporting_period_list: [],
      is_me_visible: true
    };
    this.external_entity_list.push(temp_entity);
    this.cdr.detectChanges();
  }

  removeRow(index: number): void {
    this.external_entity_list.splice(index, 1);
    this.cdr.detectChanges();
  }

  sanitizeLeid(entity: any): void {
    if (entity.leid) {
      entity.leid = entity.leid.replace(/[^a-zA-Z0-9]/g, '');
    }
  }

  save(): void {
    this.isSaveClicked = true;
    this.crudLoading = true;

    const errorFields: any[] = [];

    this.external_entity_list.forEach((row: any, key: number) => {
      const mandatoryFields = ['leid', 'ein'];
      const tempRowErrorFields: string[] = [];

      if (row.ho_leid) {
        mandatoryFields.push('ho_cdr_no');
        mandatoryFields.push('ho_reporting_period');
      } else {
        mandatoryFields.push('me_key');
      }

      mandatoryFields.forEach((val: string) => {
        if (row[val] == undefined || row[val] == null) {
          const tempValue = val.split('_');
          tempRowErrorFields.push(tempValue.join(' ').toUpperCase());
        } else if (typeof row[val] === 'string' && !row[val].trim()) {
          const tempValue = val.split('_');
          tempRowErrorFields.push(tempValue.join(' ').toUpperCase());
        }
      });

      if (tempRowErrorFields.length > 0) {
        tempRowErrorFields.unshift((key + 1).toString());
        errorFields.push(tempRowErrorFields);
      }
    });

    if (errorFields.length > 0) {
      const row = errorFields[0][0];
      const firstIndexErrorField = errorFields[0].slice(1);
      this.gtwSnackbar.openSnackBar(`On row ${row}, following fields are Mandatory: ${firstIndexErrorField.join(', ')}`, 'gtw-snackbar--red', true, 4);
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const mainData: any[] = [];
    this.external_entity_list.forEach((row: any) => {
      const tempSaveData = {
        LE_KEY: row.le_key == 'NEW' ? 0 : row.le_key,
        LE_ID: row.leid,
        LE_NAME: row.le_name,
        EIN: row.ein,
        ADDRESS_LINE1: row.address_line1,
        ADDRESS_LINE2: row.address_line2,
        ADDRESS_CITY: row.address_city,
        ADDRESS_STATE: row.address_state,
        ADDRESS_ZIP: row.address_zip,
        LE_TYPE: row.le_type,
        ME_CODE: row.ho_leid ? row.ho_leid.me_code : (row.me_key ? row.me_key.me_code : ''),
        ME_KEY: row.ho_leid ? row.ho_leid.me_key : (row.me_key ? row.me_key.me_key : ''),
        HO_LEID: row.ho_leid ? row.ho_leid.id : '',
        HO_CDR_NO: row.ho_cdr_no ? row.ho_cdr_no.id : '',
        HO_REPORTING_PERIOD: row.ho_reporting_period ? row.ho_reporting_period.id : '',
        STATE_DOMICILE: row.state_domicile,
        HO_INTERNAL_YN: row.ho_leid ? row.ho_leid.internal_flag : '',
      };
      mainData.push(tempSaveData);
    });

    let settingsData: any = {};
    if (!this.isCreateMode) {
      settingsData = this.gridData && this.gridData.length > 0
        ? (this.gridData[0].data ?? this.gridData[0])
        : {};
    }

    this.stateEntityService.saveExternalStateEntity(
      this.baseUrl,
      mainData,
      this.globalService,
      this.filterParams,
      settingsData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess == '1' && response.errorMessage == 'null') {
          this.gtwSnackbar.openSnackBar('External state entities have been successfully saved/updated', 'gtw-snackbar--green', true, 4);
          this.requestGridUpdate.emit('refresh');
        } else {
          if (response.errorMessage && response.errorMessage !== 'null') {
            this.gtwSnackbar.openSnackBar(response.errorMessage, 'gtw-snackbar--red', true, 4);
          } else {
            this.gtwSnackbar.openSnackBar('Error occurred in saving the entities. If this continues please contact support', 'gtw-snackbar--red', true, 4);
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error saving external state entities:', error);
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.detectChanges();
      }
    );
  }

  reset(): void {
    if (this.isCreateMode) {
      this.external_entity_list = [];
      this.cdr.detectChanges();
      return;
    }

    this.external_entity_list = _.cloneDeep(this.loaded_entity_list);

    this.external_entity_list.forEach((entity: any) => {
      if (entity.ho_leid) {
        const leidIndex = _.findIndex(this.leidList, { id: entity.ho_leid.id });
        entity.ho_leid = leidIndex !== -1 ? this.leidList[leidIndex] : null;
      }

      if (entity.ho_cdr_no && entity.cdr_list) {
        const cdrIndex = _.findIndex(entity.cdr_list, { id: entity.ho_cdr_no.id });
        entity.ho_cdr_no = cdrIndex !== -1 ? entity.cdr_list[cdrIndex] : null;
      }

      if (entity.ho_reporting_period && entity.reporting_period_list) {
        const rpIndex = _.findIndex(entity.reporting_period_list, { id: entity.ho_reporting_period.id });
        entity.ho_reporting_period = rpIndex !== -1 ? entity.reporting_period_list[rpIndex] : null;
      }

      if (entity.me_key) {
        const meIndex = _.findIndex(this.meList, { id: entity.me_key.id });
        entity.me_key = meIndex !== -1 ? this.meList[meIndex] : null;
      }
    });

    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
